/**
 * PWA Utilities for CTMS Frontend
 * Handles PWA installation, updates, and offline detection
 */
interface NavigatorStandalone extends Navigator {
  standalone?: boolean;
}

export interface PWAInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}
/**
 * Check if the app is running as a PWA
 */
export const isPWA = (): boolean => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as NavigatorStandalone).standalone === true ||
    document.referrer.includes("android-app://")
  );
};
/**
 * Check if PWA installation is available
 */
export const isPWAInstallable = (): boolean => {
  return "beforeinstallprompt" in window;
};
/**
 * Check if the device is online
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};
/**
 * Check if service worker is supported
 */
export const isServiceWorkerSupported = (): boolean => {
  return "serviceWorker" in navigator;
};
/**
 * Register service worker update listener
 */
export const registerSWUpdateListener = (callback: () => void): void => {
  if (isServiceWorkerSupported()) {
    navigator.serviceWorker.addEventListener("controllerchange", callback);
  }
};
/**
 * Unregister service worker update listener
 */
export const unregisterSWUpdateListener = (callback: () => void): void => {
  if (isServiceWorkerSupported()) {
    navigator.serviceWorker.removeEventListener("controllerchange", callback);
  }
};
/**
 * Force service worker update
 */
export const forceServiceWorkerUpdate = async (): Promise<void> => {
  if (isServiceWorkerSupported()) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  }
};
/**
 * Clear registered service workers and Cache Storage entries.
 *
 * This is the shared cache-reset mechanism used by the hard refresh button.
 * It can also be invoked silently after login so the user lands in a clean
 * runtime without a visible reload.
 */
export const clearServiceWorkerAndCacheStorage = async (): Promise<void> => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
  }

  if ("caches" in window) {
    const keys = await caches.keys();
    for (const key of keys) {
      await caches.delete(key);
    }
  }
};
/**
 * Add to home screen prompt
 */
export class PWAInstallManager {
  private deferredPrompt: PWAInstallPromptEvent | null = null;
  private installCallback?: () => void;
  constructor(onInstall?: () => void) {
    this.installCallback = onInstall;
    this.init();
  }
  private init(): void {
    window.addEventListener(
      "beforeinstallprompt",
      this.handleBeforeInstallPrompt.bind(this),
    );
    window.addEventListener("appinstalled", this.handleAppInstalled.bind(this));
  }
  private handleBeforeInstallPrompt(e: Event): void {
    e.preventDefault();
    this.deferredPrompt = e as PWAInstallPromptEvent;
  }
  private handleAppInstalled(): void {
    this.deferredPrompt = null;
    this.installCallback?.();
  }
  public async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }
    void this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    if (outcome === "accepted") {
      this.deferredPrompt = null;
      return true;
    }
    return false;
  }
  public isInstallPromptAvailable(): boolean {
    return this.deferredPrompt !== null;
  }
  public destroy(): void {
    window.removeEventListener(
      "beforeinstallprompt",
      this.handleBeforeInstallPrompt.bind(this),
    );
    window.removeEventListener(
      "appinstalled",
      this.handleAppInstalled.bind(this),
    );
  }
}
/**
 * Clinical-specific PWA features
 */
export const clinicalPWAFeatures = {
  /**
   * Enable offline clinical data caching
   */
  enableOfflineMode: (): void => {
    /* noop placeholder for future offline caching */
  },
  /**
   * Check if critical clinical features are available offline
   */
  isClinicalDataAvailable: (): boolean => {
    // Check if critical clinical data is cached
    return isOnline() || localStorage.getItem("clinical_cache") !== null;
  },
  /**
   * Get PWA install banner message for clinical users
   */
  getInstallMessage: (userRole?: string): string => {
    const roleMessages = {
      CRA: "Install CTMS for quick access to monitoring data during site visits",
      PI: "Install CTMS to manage your studies offline during patient consultations",
      Coordinator: "Install CTMS for rapid patient enrollment and data entry",
      default: "Install CTMS for secure offline access to clinical trial data",
    };
    return (
      roleMessages[userRole as keyof typeof roleMessages] ||
      roleMessages.default
    );
  },
};
